<template>
  <div class="ip-control-page">
    <el-tabs v-model="activeTab" @tab-click="handleTabChange">
      <el-tab-pane label="当前 IP" name="current">
        <div class="toolbar">
          <el-button type="primary" icon="el-icon-refresh" @click="loadCurrent">刷新</el-button>
          <el-button type="success" icon="el-icon-document-add" @click="openMyApply">提交申请</el-button>
        </div>
        <el-descriptions :column="3" border size="small">
          <el-descriptions-item label="当前 IP">{{ currentInfo.ip || '-' }}</el-descriptions-item>
          <el-descriptions-item label="阻断状态">
            <el-tag :type="currentInfo.blocked ? 'danger' : 'success'">
              {{ currentInfo.blocked ? '已阻断' : '未阻断' }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="授权状态">
            <el-tag v-if="currentInfo.allowed !== undefined" :type="currentInfo.allowed ? 'success' : 'warning'">
              {{ currentInfo.allowed ? '已授权' : '未授权' }}
            </el-tag>
            <span v-else>-</span>
          </el-descriptions-item>
        </el-descriptions>
      </el-tab-pane>

      <el-tab-pane label="申请审核" name="apply">
        <div class="toolbar">
          <el-input v-model="applyQuery.username" clearable placeholder="用户名" prefix-icon="el-icon-user" @keyup.enter.native="loadApply" />
          <el-select v-model="applyQuery.status" clearable placeholder="状态">
            <el-option label="待审核" value="PENDING" />
            <el-option label="已通过" value="APPROVED" />
            <el-option label="已拒绝" value="REJECTED" />
          </el-select>
          <el-button type="primary" icon="el-icon-search" @click="loadApply">查询</el-button>
          <el-button icon="el-icon-refresh-left" @click="resetApply">重置</el-button>
        </div>
        <el-table :data="applyData" border stripe :header-cell-class-name="'headerBg'">
          <el-table-column prop="id" label="ID" width="80" />
          <el-table-column prop="username" label="用户" width="120" />
          <el-table-column prop="ip" label="IP" min-width="160" />
          <el-table-column prop="reason" label="申请原因" min-width="180" show-overflow-tooltip />
          <el-table-column prop="applicantRemark" label="备注" min-width="160" show-overflow-tooltip />
          <el-table-column prop="status" label="状态" width="100">
            <template slot-scope="scope">
              <el-tag :type="statusType(scope.row.status)">{{ statusText(scope.row.status) }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="createTime" label="申请时间" min-width="160">
            <template slot-scope="scope">{{ formatTime(scope.row.createTime) }}</template>
          </el-table-column>
          <el-table-column label="操作" width="180" fixed="right">
            <template slot-scope="scope">
              <el-button size="mini" type="success" icon="el-icon-check" :disabled="scope.row.status !== 'PENDING'" @click="openReview(scope.row, 'approve')">通过</el-button>
              <el-button size="mini" type="danger" icon="el-icon-close" :disabled="scope.row.status !== 'PENDING'" @click="openReview(scope.row, 'reject')">拒绝</el-button>
            </template>
          </el-table-column>
        </el-table>
        <el-pagination class="pager" :current-page="applyQuery.pageNum" :page-size="applyQuery.pageSize" :page-sizes="pageSizes" :total="applyTotal" layout="total, sizes, prev, pager, next, jumper" @size-change="size => changeSize('apply', size)" @current-change="page => changePage('apply', page)" />
      </el-tab-pane>

      <el-tab-pane label="授权列表" name="auth">
        <div class="toolbar">
          <el-input v-model="authQuery.username" clearable placeholder="用户名" prefix-icon="el-icon-user" @keyup.enter.native="loadAuth" />
          <el-input v-model="authQuery.ip" clearable placeholder="IP / CIDR / ALL" prefix-icon="el-icon-location-outline" @keyup.enter.native="loadAuth" />
          <el-button type="primary" icon="el-icon-search" @click="loadAuth">查询</el-button>
          <el-button icon="el-icon-refresh-left" @click="resetAuth">重置</el-button>
          <el-button type="success" icon="el-icon-plus" @click="openAuth()">新增授权</el-button>
        </div>
        <el-table :data="authData" border stripe :header-cell-class-name="'headerBg'">
          <el-table-column prop="id" label="ID" width="80" />
          <el-table-column prop="userId" label="用户ID" width="90" />
          <el-table-column prop="ip" label="IP / CIDR" min-width="160" />
          <el-table-column prop="ipType" label="类型" width="100" />
          <el-table-column prop="status" label="状态" width="100">
            <template slot-scope="scope">
              <el-tag :type="scope.row.status === 'ALLOWED' ? 'success' : 'info'">{{ scope.row.status }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="reason" label="原因" min-width="180" show-overflow-tooltip />
          <el-table-column prop="expireTime" label="过期时间" min-width="160">
            <template slot-scope="scope">{{ formatTime(scope.row.expireTime) }}</template>
          </el-table-column>
          <el-table-column label="操作" width="190" fixed="right">
            <template slot-scope="scope">
              <el-button size="mini" type="primary" icon="el-icon-edit" @click="openAuth(scope.row)">编辑</el-button>
              <el-button size="mini" type="danger" icon="el-icon-delete" :disabled="scope.row.status === 'DISABLED'" @click="disableAuth(scope.row)">停用</el-button>
            </template>
          </el-table-column>
        </el-table>
        <el-pagination class="pager" :current-page="authQuery.pageNum" :page-size="authQuery.pageSize" :page-sizes="pageSizes" :total="authTotal" layout="total, sizes, prev, pager, next, jumper" @size-change="size => changeSize('auth', size)" @current-change="page => changePage('auth', page)" />
      </el-tab-pane>

      <el-tab-pane label="黑名单" name="block">
        <div class="toolbar">
          <el-input v-model="blockQuery.ip" clearable placeholder="IP / CIDR" prefix-icon="el-icon-location-outline" @keyup.enter.native="loadBlock" />
          <el-select v-model="blockQuery.enabled" clearable placeholder="状态">
            <el-option label="启用" :value="1" />
            <el-option label="停用" :value="0" />
          </el-select>
          <el-button type="primary" icon="el-icon-search" @click="loadBlock">查询</el-button>
          <el-button icon="el-icon-refresh-left" @click="resetBlock">重置</el-button>
          <el-button type="danger" icon="el-icon-plus" @click="openBlock()">新增阻断</el-button>
        </div>
        <el-table :data="blockData" border stripe :header-cell-class-name="'headerBg'">
          <el-table-column prop="id" label="ID" width="80" />
          <el-table-column prop="ip" label="IP / CIDR" min-width="160" />
          <el-table-column prop="ipType" label="类型" width="100" />
          <el-table-column prop="enabled" label="状态" width="100">
            <template slot-scope="scope">
              <el-tag :type="scope.row.enabled === 1 ? 'danger' : 'info'">{{ scope.row.enabled === 1 ? '启用' : '停用' }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="reason" label="原因" min-width="180" show-overflow-tooltip />
          <el-table-column prop="expireTime" label="过期时间" min-width="160">
            <template slot-scope="scope">{{ formatTime(scope.row.expireTime) }}</template>
          </el-table-column>
          <el-table-column label="操作" width="100" fixed="right">
            <template slot-scope="scope">
              <el-button size="mini" type="success" icon="el-icon-unlock" :disabled="scope.row.enabled !== 1" @click="unblock(scope.row)">解除</el-button>
            </template>
          </el-table-column>
        </el-table>
        <el-pagination class="pager" :current-page="blockQuery.pageNum" :page-size="blockQuery.pageSize" :page-sizes="pageSizes" :total="blockTotal" layout="total, sizes, prev, pager, next, jumper" @size-change="size => changeSize('block', size)" @current-change="page => changePage('block', page)" />
      </el-tab-pane>

      <el-tab-pane label="登录日志" name="log">
        <div class="toolbar">
          <el-input v-model="logQuery.username" clearable placeholder="用户名" prefix-icon="el-icon-user" @keyup.enter.native="loadLog" />
          <el-input v-model="logQuery.ip" clearable placeholder="IP" prefix-icon="el-icon-location-outline" @keyup.enter.native="loadLog" />
          <el-select v-model="logQuery.result" clearable placeholder="结果">
            <el-option label="成功" value="SUCCESS" />
            <el-option label="密码错误" value="PASSWORD_ERROR" />
            <el-option label="IP 阻断" value="IP_BLOCKED" />
            <el-option label="IP 未授权" value="IP_NOT_ALLOWED" />
          </el-select>
          <el-button type="primary" icon="el-icon-search" @click="loadLog">查询</el-button>
          <el-button icon="el-icon-refresh-left" @click="resetLog">重置</el-button>
        </div>
        <el-table :data="logData" border stripe :header-cell-class-name="'headerBg'">
          <el-table-column prop="id" label="ID" width="80" />
          <el-table-column prop="username" label="用户" width="120" />
          <el-table-column prop="ip" label="IP" min-width="160" />
          <el-table-column prop="loginResult" label="结果" width="130">
            <template slot-scope="scope">
              <el-tag :type="logType(scope.row.loginResult)">{{ logText(scope.row.loginResult) }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="failReason" label="原因" min-width="180" show-overflow-tooltip />
          <el-table-column prop="userAgent" label="User Agent" min-width="220" show-overflow-tooltip />
          <el-table-column prop="loginTime" label="登录时间" min-width="160">
            <template slot-scope="scope">{{ formatTime(scope.row.loginTime) }}</template>
          </el-table-column>
        </el-table>
        <el-pagination class="pager" :current-page="logQuery.pageNum" :page-size="logQuery.pageSize" :page-sizes="pageSizes" :total="logTotal" layout="total, sizes, prev, pager, next, jumper" @size-change="size => changeSize('log', size)" @current-change="page => changePage('log', page)" />
      </el-tab-pane>
    </el-tabs>

    <el-dialog title="提交 IP 申请" :visible.sync="applyDialogVisible" width="420px">
      <el-form :model="myApplyForm" label-width="80px">
        <el-form-item label="用户名">
          <el-input v-model="myApplyForm.username" placeholder="未登录时必填" />
        </el-form-item>
        <el-form-item label="原因">
          <el-input v-model="myApplyForm.reason" />
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="myApplyForm.applicantRemark" type="textarea" :rows="3" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="applyDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitMyApply">提交</el-button>
      </span>
    </el-dialog>

    <el-dialog :title="reviewTitle" :visible.sync="reviewDialogVisible" width="420px">
      <el-form :model="reviewForm" label-width="90px">
        <el-form-item v-if="reviewAction === 'approve'" label="过期时间">
          <el-date-picker v-model="reviewForm.expireTime" type="datetime" value-format="yyyy-MM-dd HH:mm:ss" placeholder="不填则长期有效" style="width: 100%" />
        </el-form-item>
        <el-form-item label="审核备注">
          <el-input v-model="reviewForm.reviewerRemark" type="textarea" :rows="3" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="reviewDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitReview">确定</el-button>
      </span>
    </el-dialog>

    <el-dialog :title="authForm.id ? '编辑授权' : '新增授权'" :visible.sync="authDialogVisible" width="460px">
      <el-form :model="authForm" label-width="90px">
        <el-form-item label="用户ID">
          <el-input-number v-model="authForm.userId" :min="1" controls-position="right" style="width: 100%" />
        </el-form-item>
        <el-form-item label="IP">
          <el-input v-model="authForm.ip" placeholder="例如 192.168.1.10、192.168.1.0/24 或 ALL" />
        </el-form-item>
        <el-form-item label="类型">
          <el-select v-model="authForm.ipType" style="width: 100%">
            <el-option label="单 IP" value="SINGLE" />
            <el-option label="CIDR 网段" value="CIDR" />
          </el-select>
        </el-form-item>
        <el-form-item label="过期时间">
          <el-date-picker v-model="authForm.expireTime" type="datetime" value-format="yyyy-MM-dd HH:mm:ss" placeholder="不填则长期有效" style="width: 100%" />
        </el-form-item>
        <el-form-item label="原因">
          <el-input v-model="authForm.reason" type="textarea" :rows="3" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="authDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="saveAuth">保存</el-button>
      </span>
    </el-dialog>

    <el-dialog title="新增阻断" :visible.sync="blockDialogVisible" width="460px">
      <el-form :model="blockForm" label-width="90px">
        <el-form-item label="IP">
          <el-input v-model="blockForm.ip" placeholder="例如 10.0.0.8 或 10.0.0.0/24" />
        </el-form-item>
        <el-form-item label="类型">
          <el-select v-model="blockForm.ipType" style="width: 100%">
            <el-option label="单 IP" value="SINGLE" />
            <el-option label="CIDR 网段" value="CIDR" />
          </el-select>
        </el-form-item>
        <el-form-item label="过期时间">
          <el-date-picker v-model="blockForm.expireTime" type="datetime" value-format="yyyy-MM-dd HH:mm:ss" placeholder="不填则长期有效" style="width: 100%" />
        </el-form-item>
        <el-form-item label="原因">
          <el-input v-model="blockForm.reason" type="textarea" :rows="3" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="blockDialogVisible = false">取消</el-button>
        <el-button type="danger" @click="saveBlock">保存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import request from "@/utils/request.js";
export default {
  name: 'IpControl',
  data() {
    return {
      activeTab: 'current',
      pageSizes: [10, 20, 50, 100],
      currentInfo: {},
      applyData: [],
      applyTotal: 0,
      applyQuery: { pageNum: 1, pageSize: 10, username: '', status: 'PENDING' },
      authData: [],
      authTotal: 0,
      authQuery: { pageNum: 1, pageSize: 10, username: '', ip: '' },
      blockData: [],
      blockTotal: 0,
      blockQuery: { pageNum: 1, pageSize: 10, ip: '', enabled: 1 },
      logData: [],
      logTotal: 0,
      logQuery: { pageNum: 1, pageSize: 10, username: '', ip: '', result: '' },
      applyDialogVisible: false,
      myApplyForm: { username: '', reason: '', applicantRemark: '' },
      reviewDialogVisible: false,
      reviewAction: '',
      reviewRow: null,
      reviewForm: { reviewerRemark: '', expireTime: '' },
      authDialogVisible: false,
      authForm: { id: null, userId: null, ip: '', ipType: 'SINGLE', expireTime: '', reason: '' },
      blockDialogVisible: false,
      blockForm: { ip: '', ipType: 'SINGLE', expireTime: '', reason: '' }
    }
  },
  computed: {
    reviewTitle() {
      return this.reviewAction === 'approve' ? '审批通过' : '拒绝申请'
    }
  },
  created() {
    this.loadCurrent()
  },
  methods: {
    handleTabChange() {
      if (this.activeTab === 'current') this.loadCurrent()
      if (this.activeTab === 'apply') this.loadApply()
      if (this.activeTab === 'auth') this.loadAuth()
      if (this.activeTab === 'block') this.loadBlock()
      if (this.activeTab === 'log') this.loadLog()
    },
    loadCurrent() {
      request.get('/ip/current').then(res => {
        if (res.code === '200') this.currentInfo = res.data || {}
      })
    },
    loadApply() {
      request.get('/admin/ip/apply/list', { params: this.applyQuery }).then(res => {
        if (res.code === '200') {
          this.applyData = res.data.records || []
          this.applyTotal = res.data.total || 0
        }
      })
    },
    loadAuth() {
      request.get('/admin/ip/auth/list', { params: this.authQuery }).then(res => {
        if (res.code === '200') {
          this.authData = res.data.records || []
          this.authTotal = res.data.total || 0
        }
      })
    },
    loadBlock() {
      request.get('/admin/ip/block/list', { params: this.blockQuery }).then(res => {
        if (res.code === '200') {
          this.blockData = res.data.records || []
          this.blockTotal = res.data.total || 0
        }
      })
    },
    loadLog() {
      request.get('/admin/ip/login-log', { params: this.logQuery }).then(res => {
        if (res.code === '200') {
          this.logData = res.data.records || []
          this.logTotal = res.data.total || 0
        }
      })
    },
    openMyApply() {
      const user = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')) : {}
      this.myApplyForm = { username: user.username || '', reason: '', applicantRemark: '' }
      this.applyDialogVisible = true
    },
    submitMyApply() {
      request.post('/ip/apply', this.myApplyForm).then(res => {
        if (res.code === '200') {
          this.$message.success('提交成功')
          this.applyDialogVisible = false
          this.loadApply()
        } else {
          this.$message.error(res.msg)
        }
      })
    },
    openReview(row, action) {
      this.reviewRow = row
      this.reviewAction = action
      this.reviewForm = { reviewerRemark: '', expireTime: '' }
      this.reviewDialogVisible = true
    },
    submitReview() {
      const url = `/admin/ip/apply/${this.reviewRow.id}/${this.reviewAction}`
      request.post(url, this.reviewForm).then(res => {
        if (res.code === '200') {
          this.$message.success('操作成功')
          this.reviewDialogVisible = false
          this.loadApply()
          this.loadAuth()
        } else {
          this.$message.error(res.msg)
        }
      })
    },
    openAuth(row) {
      this.authForm = row ? {
        id: row.id,
        userId: row.userId,
        ip: row.ip,
        ipType: row.ipType || 'SINGLE',
        expireTime: this.formatPickerTime(row.expireTime),
        reason: row.reason || ''
      } : { id: null, userId: null, ip: '', ipType: 'SINGLE', expireTime: '', reason: '' }
      this.authDialogVisible = true
    },
    saveAuth() {
      if (!this.authForm.userId || !this.authForm.ip) {
        this.$message.warning('请填写用户ID和IP')
        return
      }
      request.post('/admin/ip/auth/allow', this.authForm).then(res => {
        if (res.code === '200') {
          this.$message.success('保存成功')
          this.authDialogVisible = false
          this.loadAuth()
        } else {
          this.$message.error(res.msg)
        }
      })
    },
    disableAuth(row) {
      this.$confirm('确认停用该授权记录？', '提示', { type: 'warning' }).then(() => {
        request.delete(`/admin/ip/auth/${row.id}`).then(res => {
          if (res.code === '200') {
            this.$message.success('已停用')
            this.loadAuth()
          } else {
            this.$message.error(res.msg)
          }
        })
      }).catch(() => {})
    },
    openBlock() {
      this.blockForm = { ip: '', ipType: 'SINGLE', expireTime: '', reason: '' }
      this.blockDialogVisible = true
    },
    saveBlock() {
      if (!this.blockForm.ip) {
        this.$message.warning('请填写IP')
        return
      }
      request.post('/admin/ip/block', this.blockForm).then(res => {
        if (res.code === '200') {
          this.$message.success('保存成功')
          this.blockDialogVisible = false
          this.loadBlock()
        } else {
          this.$message.error(res.msg)
        }
      })
    },
    unblock(row) {
      this.$confirm('确认解除该阻断记录？', '提示', { type: 'warning' }).then(() => {
        request.delete(`/admin/ip/block/${row.id}`).then(res => {
          if (res.code === '200') {
            this.$message.success('已解除')
            this.loadBlock()
          } else {
            this.$message.error(res.msg)
          }
        })
      }).catch(() => {})
    },
    changeSize(type, size) {
      this[`${type}Query`].pageSize = size
      this[`${type}Query`].pageNum = 1
      this[`load${type.charAt(0).toUpperCase() + type.slice(1)}`]()
    },
    changePage(type, page) {
      this[`${type}Query`].pageNum = page
      this[`load${type.charAt(0).toUpperCase() + type.slice(1)}`]()
    },
    resetApply() {
      this.applyQuery = { pageNum: 1, pageSize: 10, username: '', status: 'PENDING' }
      this.loadApply()
    },
    resetAuth() {
      this.authQuery = { pageNum: 1, pageSize: 10, username: '', ip: '' }
      this.loadAuth()
    },
    resetBlock() {
      this.blockQuery = { pageNum: 1, pageSize: 10, ip: '', enabled: 1 }
      this.loadBlock()
    },
    resetLog() {
      this.logQuery = { pageNum: 1, pageSize: 10, username: '', ip: '', result: '' }
      this.loadLog()
    },
    statusText(status) {
      const map = { PENDING: '待审核', APPROVED: '已通过', REJECTED: '已拒绝' }
      return map[status] || status
    },
    statusType(status) {
      const map = { PENDING: 'warning', APPROVED: 'success', REJECTED: 'danger' }
      return map[status] || 'info'
    },
    logText(result) {
      const map = { SUCCESS: '成功', PASSWORD_ERROR: '密码错误', IP_BLOCKED: 'IP 阻断', IP_NOT_ALLOWED: 'IP 未授权' }
      return map[result] || result
    },
    logType(result) {
      const map = { SUCCESS: 'success', PASSWORD_ERROR: 'warning', IP_BLOCKED: 'danger', IP_NOT_ALLOWED: 'danger' }
      return map[result] || 'info'
    },
    formatTime(value) {
      if (!value) return '-'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return value
      const pad = n => `${n}`.padStart(2, '0')
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
    },
    formatPickerTime(value) {
      return value ? this.formatTime(value) : ''
    }
  }
}
</script>

<style scoped>
.ip-control-page {
  padding: 4px;
}
.toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  margin-bottom: 12px;
}
.toolbar .el-input {
  width: 220px;
}
.toolbar .el-select {
  width: 150px;
}
.pager {
  margin-top: 12px;
  text-align: right;
}
</style>
